import { DataDrivenPropertyValueSpecification, ExpressionSpecification } from "maplibre-gl";

/**
 * Extracts a representative static value from a MapLibre PropertyValueSpecification or
 * DataDrivenPropertyValueSpecification for use in legend display.
 *
 * We do NOT evaluate expressions — we extract the fallback/default so the legend shows
 * the "rest-state" value without pretending to evaluate against feature data or a zoom level:
 *
 *   - Static value (string/number)  → returned as-is.
 *   - `["literal", v]`              → returns v.
 *   - `["match", …, fallback]`      → returns the trailing fallback (last element).
 *   - `["case", …, fallback]`       → same — returns the trailing fallback.
 *   - `["step", input, default, …]` → returns the default output before the first stop (index 2).
 *   - `["interpolate", …]`          → returns the value at the first stop (index 4).
 *   - `["coalesce", …]`             → returns the first argument that is a static value.
 *   - Legacy function object        → returns the `default` field, or the first stop value.
 *   - Everything else               → returns undefined (e.g. `get`, `zoom`, arithmetic).
 */
function extractDefault<T extends string | number>(
  spec: DataDrivenPropertyValueSpecification<T> | undefined
): T | undefined {
  if (spec === undefined || spec === null) return undefined;

  // Static value — most common case for legend paint properties
  if (typeof spec === "string" || typeof spec === "number") return spec as T;

  // Legacy function object: { type, stops, default }
  if (!Array.isArray(spec)) {
    const fn = spec as { default?: unknown; stops?: Array<[unknown, unknown]> };
    if (fn.default !== undefined) return fn.default as T;
    if (fn.stops && fn.stops.length > 0) return fn.stops[0][1] as T;
    return undefined;
  }

  // Expression array
  const expr = spec as ExpressionSpecification;
  switch (expr[0]) {
    case "literal":
      // ["literal", value]
      return expr[1] as T;

    case "match":
      // ["match", input, v1, out1, …, fallback] — fallback is the last element
      return expr[expr.length - 1] as T;

    case "case":
      // ["case", cond1, out1, …, fallback] — fallback is the last element
      return expr[expr.length - 1] as T;

    case "step":
      // ["step", input, default_output, stop1, out1, …] — default_output is index 2
      return expr[2] as T;

    // interpolate: RGB (default), HCL (perceptually uniform cylindrical), LAB (perceptually uniform)
    // All three have the same structure; we only care about the first stop value.
    case "interpolate":
    case "interpolate-hcl":
    case "interpolate-lab":
      // ["interpolate", interp, input, stop0, val0, stop1, val1, …] — first stop value is index 4
      return expr[4] as T;

    case "coalesce":
      // Return the first statically-known value
      for (let i = 1; i < expr.length; i++) {
        const v = expr[i];
        if (typeof v === "string" || typeof v === "number") return v as T;
      }
      return undefined;

    default:
      return undefined;
  }
}

/** Returns the representative static number from a paint/layout value, or undefined. */
export function evalNumber(
  expr: DataDrivenPropertyValueSpecification<number> | undefined
): number | undefined {
  const v = extractDefault<number>(expr);
  return typeof v === "number" ? v : undefined;
}

/** Returns the representative static color string from a paint value, or undefined. */
export function evalColor(
  expr: DataDrivenPropertyValueSpecification<string> | undefined
): string | undefined {
  const v = extractDefault<string>(expr);
  return typeof v === "string" ? v : undefined;
}
